<template>
  <!-- Hidden when GOOGLE_CLIENT_ID is not set (e.g. on-prem deploys) -->
  <div v-if="clientId" class="google-btn-wrapper">
    <v-btn
      class="google-btn"
      type="button"
      :disabled="!ready"
      @click="triggerPrompt"
    >
      <v-icon left>mdi-google</v-icon>
      {{ label }}
    </v-btn>
  </div>
</template>

<script>
/**
 * Renders a custom Google login button that triggers One Tap.
 *
 * Instead of opening a new popup window, clicking the button closes and
 * re-opens the One Tap prompt (top-right).
 *
 * The button is automatically hidden when GOOGLE_CLIENT_ID is not configured
 * (e.g. on-prem deployments that don't need Google auth).
 *
 * Props:
 *   - text: "signin_with" | "signup_with" | "continue_with" (default)
 *
 * Events:
 *   - credential(jwt): emitted with the Google ID token on success
 *   - error(msg): emitted on failure
 */

import {
  GOOGLE_CLIENT_ID,
  initializeGoogle,
  loadScript,
  promptOneTap,
  setOneTimeHandler
} from "../google-auth";

export default {
  name: "GoogleLoginButton",
  props: {
    text: {
      type: String,
      default: "continue_with",
      validator: v => ["signin_with", "signup_with", "continue_with"].includes(v)
    }
  },
  data: () => ({
    clientId: GOOGLE_CLIENT_ID,
    initialized: false,
    ready: false,
    promptTimer: null
  }),
  computed: {
    label() {
      if (this.text === "signin_with") return "Sign in with Google";
      if (this.text === "signup_with") return "Sign up with Google";
      return "Continue with Google";
    }
  },
  mounted() {
    if (this.clientId) {
      loadScript()
        .then(() => {
          this.initialize();
          this.ready = true;
        })
        .catch(() => {
          this.$emit("error", "Google authentication unavailable");
        });
    }
  },
  beforeDestroy() {
    this.clearPromptTimer();
  },
  methods: {
    clearPromptTimer() {
      if (!this.promptTimer) return;
      clearTimeout(this.promptTimer);
      this.promptTimer = null;
    },
    initialize() {
      if (this.initialized || !window.google || !window.google.accounts) return;
      this.initialized = true;
      initializeGoogle(this.clientId);
    },
    triggerPrompt() {
      loadScript()
        .then(() => {
          this.initialize();
          if (window.google && window.google.accounts) {
            const restoreHandler = setOneTimeHandler(this.onCredentialResponse);
            window.google.accounts.id.cancel();
            this.clearPromptTimer();
            // Small delay helps the prompt re-open after cancel.
            this.promptTimer = setTimeout(() => {
              promptOneTap(notification => {
                if (
                  notification.isNotDisplayed?.() ||
                  notification.isSkippedMoment?.()
                ) {
                  // One Tap might be in cooldown (suppressed by user or browser).
                  // Restore the previous handler when prompt didn't show.
                  restoreHandler();
                }
              });
            }, 150);
          }
        })
        .catch(() => {
          this.$emit("error", "Failed to load Google Identity Services");
        });
    },

    onCredentialResponse(response) {
      if (response && response.credential) {
        this.$emit("credential", response.credential);
      } else {
        this.$emit("error", "Google authentication failed");
      }
    }
  }
};
</script>

<style scoped>
.google-btn-wrapper {
  display: flex;
  justify-content: center;
}

.google-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  background-color: #1f1f1f !important;
  color: #fff !important;
  width: 100%;
  max-width: 360px;
}
</style>
